import React, { createContext, useContext, useState, useMemo, ReactNode } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

interface ThemeContextType {
    toggleColorMode: () => void;
    mode: "light" | "dark";
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error("useTheme must be used within a ThemeContextProvider");
    }
    return context;
};

interface ThemeContextProviderProps {
    children: ReactNode;
}

export function ThemeContextProvider({ children }: ThemeContextProviderProps) {
    const [mode, setMode] = useState<"light" | "dark">("dark");

    const toggleColorMode = () => {
        setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
    };

    const theme = useMemo(
        () =>
            createTheme({
                palette: {
                    mode,
                    ...(mode === "light"
                        ? {
                              primary: {
                                  main: "#1976d2",
                              },
                              secondary: {
                                  main: "#dc004e",
                              },
                              background: {
                                  default: "#ffffff",
                                  paper: "#f5f5f5",
                              },
                          }
                        : {
                              primary: {
                                  main: "#90caf9",
                                  light: "#e3f2fd",
                                  dark: "#42a5f5",
                              },
                              secondary: {
                                  main: "#f48fb1",
                                  light: "#fce4ec",
                                  dark: "#c2185b",
                              },
                              background: {
                                  default: "#121212",
                                  paper: "#1e1e1e",
                              },
                              text: {
                                  primary: "#ffffff",
                                  secondary: "rgba(255, 255, 255, 0.7)",
                              },
                              divider: "rgba(255, 255, 255, 0.12)",
                          }),
                },
                components: {
                    MuiInputBase: {
                        styleOverrides: {
                            input: {
                                fontSize: "0.85rem",
                            },
                        },
                    },
                    MuiInputLabel: {
                        styleOverrides: {
                            root: {
                                fontSize: "0.85rem",
                            },
                        },
                    },
                    MuiFormControlLabel: {
                        styleOverrides: {
                            label: {
                                fontSize: "0.85rem",
                            },
                        },
                    },
                    MuiCssBaseline: {
                        styleOverrides: {
                            body: {
                                scrollbarColor: mode === "dark" ? "#555 #1e1e1e" : "#bbb #f5f5f5",
                                "&::-webkit-scrollbar, & *::-webkit-scrollbar": {
                                    width: "8px",
                                    height: "8px",
                                },
                                "&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track": {
                                    backgroundColor: mode === "dark" ? "#1e1e1e" : "#f5f5f5",
                                    borderRadius: "4px",
                                },
                                "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
                                    backgroundColor: mode === "dark" ? "#555" : "#bbb",
                                    borderRadius: "4px",
                                    "&:hover": {
                                        backgroundColor: mode === "dark" ? "#777" : "#999",
                                    },
                                },
                            },
                        },
                    },
                    ...(mode === "dark" && {
                        MuiPaper: {
                            styleOverrides: {
                                root: {
                                    backgroundImage: "none",
                                },
                            },
                        },
                        MuiAppBar: {
                            styleOverrides: {
                                root: {
                                    backgroundImage: "none",
                                    backgroundColor: "#1e1e1e",
                                },
                            },
                        },
                        MuiDrawer: {
                            styleOverrides: {
                                paper: {
                                    backgroundImage: "none",
                                    backgroundColor: "#1e1e1e",
                                },
                            },
                        },
                        MuiCard: {
                            styleOverrides: {
                                root: {
                                    backgroundImage: "none",
                                    backgroundColor: "#1e1e1e",
                                },
                            },
                        },
                        MuiTableCell: {
                            styleOverrides: {
                                root: {
                                    borderColor: "rgba(255, 255, 255, 0.12)",
                                },
                            },
                        },
                        MuiDivider: {
                            styleOverrides: {
                                root: {
                                    borderColor: "rgba(255, 255, 255, 0.12)",
                                },
                            },
                        },
                    }),
                },
            }),
        [mode],
    );

    const contextValue = useMemo(
        () => ({
            toggleColorMode,
            mode,
        }),
        [mode],
    );

    return (
        <ThemeContext.Provider value={contextValue}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </ThemeProvider>
        </ThemeContext.Provider>
    );
}
